import {
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  LocalDining as LocalDiningIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Remove as RemoveIcon,
  Schedule as ScheduleIcon,
  ShoppingCart as CartIcon,
  TableRestaurant as TableIcon,
} from '../icons';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatShortDate, formatTime, formatShortDateTime } from '../../utils/dateUtils';
import orderService from '../../services/orderService';
import paymentService from '../../services/paymentService';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { ORDER_STATUS_CONFIG, getOrderStatusColor, getOrderStatusLabel, getPaymentStatusConfig } from '../../config';
import AddOrderItemDialog from './AddOrderItemDialog';
import GcashReceiptPreview from '../payment/GcashReceiptPreview';

const findGcashReceiptPayment = (payments) => {
  const list = Array.isArray(payments) ? payments : payments?.results || [];
  return (
    list.find(
      (p) =>
        p.method === 'gcash' &&
        (p.receipt_image_url ||
          (p.receipt_data && Object.keys(p.receipt_data).length > 0))
    ) || null
  );
};

const STATUS_OPTIONS = Object.entries(ORDER_STATUS_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
  color: cfg.muiColor,
}));

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return <AccessTimeIcon sx={{ fontSize: '1rem' }} />;
    case 'confirmed':
    case 'preparing':
      return <LocalDiningIcon sx={{ fontSize: '1rem' }} />;
    case 'ready':
    case 'delivered':
    case 'completed':
      return <CheckCircleIcon sx={{ fontSize: '1rem' }} />;
    case 'cancelled':
      return <CancelIcon sx={{ fontSize: '1rem' }} />;
    default:
      return <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />;
  }
};

const InfoRow = ({ icon, label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
    <Box sx={{
      width: 32,
      height: 32,
      borderRadius: '8px',
      bgcolor: 'rgba(102,126,234,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      mt: 0.25,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: '1rem', color: '#667eea' } })}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>
        {children}
      </Box>
    </Box>
  </Box>
);

const OrderDetailDialog = ({
  open,
  onClose,
  orderId,
  dialogType = 'view',
  onStatusUpdate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { formatCurrency } = useCurrencyFormatter();
  const { canAddEditOrderItems } = useMerchant();

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [gcashPayment, setGcashPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFormData, setStatusFormData] = useState({ status: '', notes: '' });
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchOrderDetails = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      const [orderData, orderItemsData, historyData, paymentsData] = await Promise.all([
        orderService.getOrder(id),
        orderService.getOrderItems({ order: id }),
        orderService.getOrderStatusHistory(id),
        // Receipt preview is best-effort; never let a payments failure break the dialog.
        paymentService.getPayments({ order: id }).catch(() => []),
      ]);

      setSelectedOrder(orderData);
      setOrderItems(
        Array.isArray(orderItemsData)
          ? orderItemsData
          : orderItemsData.results || []
      );
      setStatusHistory(
        Array.isArray(historyData) ? historyData : historyData.results || []
      );
      setGcashPayment(findGcashReceiptPayment(paymentsData));
      setStatusFormData({ status: orderData.status, notes: '' });
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails(orderId);
    }
    if (!open) {
      setSelectedOrder(null);
      setOrderItems([]);
      setStatusHistory([]);
      setGcashPayment(null);
      setError(null);
    }
  }, [open, orderId, fetchOrderDetails]);

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    try {
      await orderService.updateOrderStatus(
        selectedOrder.id,
        statusFormData.status,
        statusFormData.notes
      );
      if (onStatusUpdate) {
        onStatusUpdate(selectedOrder.id, statusFormData.status);
      }
      onClose();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update order status.');
    }
  };

  const isPending = selectedOrder?.status === 'pending';

  const refreshOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const [orderData, orderItemsData] = await Promise.all([
        orderService.getOrder(orderId),
        orderService.getOrderItems({ order: orderId }),
      ]);
      setSelectedOrder(orderData);
      setOrderItems(
        Array.isArray(orderItemsData)
          ? orderItemsData
          : orderItemsData.results || []
      );
      if (onStatusUpdate) onStatusUpdate();
    } catch (err) {
      console.error('Error refreshing order:', err);
    }
  }, [orderId, onStatusUpdate]);

  const handleDeleteItem = async (itemId) => {
    try {
      setDeletingItemId(itemId);
      await orderService.deleteOrderItem(itemId);
      setSnackbar({ open: true, message: 'Item removed', severity: 'success' });
      await refreshOrder();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSnackbar({ open: true, message: detail || 'Failed to remove item', severity: 'error' });
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleUpdateQuantity = async (item, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      setUpdatingItemId(item.id);
      await orderService.updateOrderItem(item.id, { quantity: newQuantity });
      await refreshOrder();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSnackbar({ open: true, message: detail || 'Failed to update quantity', severity: 'error' });
    } finally {
      setUpdatingItemId(null);
    }
  };

  const handleItemAdded = async () => {
    setSnackbar({ open: true, message: 'Item added', severity: 'success' });
    await refreshOrder();
  };

  const handleClose = () => {
    if (isPending && orderItems.length === 0 && selectedOrder) {
      setSnackbar({ open: true, message: 'Please add at least one item before closing', severity: 'warning' });
      return;
    }
    onClose();
  };

  const getTableName = (order) => {
    if (typeof order.table === 'object' && order.table?.name) {
      return order.table.name;
    }
    if (order.table_name) return order.table_name;
    if (order.table && typeof order.table !== 'object') return order.table;
    return 'No Table';
  };

  const statusClr = selectedOrder ? getOrderStatusColor(selectedOrder.status) : { color: '#667eea', bg: '#eef2ff' };
  const pmtCfg = selectedOrder?.payment_status ? getPaymentStatusConfig(selectedOrder.payment_status) : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '16px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          maxHeight: isMobile ? '100%' : 'min(88vh, 680px)',
          display: 'flex',
          flexDirection: 'column',
          m: isMobile ? 0 : { xs: 2, sm: 3 },
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
        <Box sx={{
          background: '#667eea',
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2.25, sm: 2.5 },
          pb: { xs: 1.75, sm: 2 },
          position: 'relative',
        }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'rgba(255,255,255,0.8)',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            mb: 0.5,
          }}>
            {dialogType === 'view' ? 'Order Details' : 'Update Order Status'}
          </Typography>

          {selectedOrder && (
            <Typography sx={{
              fontWeight: 900,
              fontSize: { xs: '1.35rem', sm: '1.5rem' },
              color: 'white',
              lineHeight: 1.2,
            }}>
              #{selectedOrder.order_number}
            </Typography>
          )}
        </Box>

        {/* ── Status Badges Strip ── */}
        {selectedOrder && (
          <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.25, sm: 1.25 },
            bgcolor: '#f9fafb',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}>
            <Chip
              label={getOrderStatusLabel(selectedOrder.status)}
              size="small"
              sx={{
                bgcolor: statusClr.bg,
                color: statusClr.color,
                fontWeight: 700,
                fontSize: '0.72rem',
                height: '26px',
              }}
            />
            {pmtCfg && (
              <Chip
                icon={selectedOrder.payment_status.toUpperCase() === 'COMPLETED'
                  ? <CheckCircleIcon sx={{ fontSize: '0.75rem !important', color: `${pmtCfg.color} !important` }} />
                  : undefined
                }
                label={pmtCfg.label}
                size="small"
                sx={{
                  bgcolor: pmtCfg.bg,
                  color: pmtCfg.color,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: '26px',
                }}
              />
            )}
            {selectedOrder.order_type && (
              <Chip
                label={selectedOrder.order_type === 'dine_in' ? 'Dine-In' : selectedOrder.order_type === 'takeout' ? 'Takeout' : selectedOrder.order_type}
                size="small"
                sx={{
                  bgcolor: selectedOrder.order_type === 'dine_in' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                  color: selectedOrder.order_type === 'dine_in' ? '#3b82f6' : '#f59e0b',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: '26px',
                }}
              />
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#667eea' }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 5, px: 2.5 }}>
            <Typography color="error" sx={{ fontWeight: 600 }}>{error}</Typography>
          </Box>
        ) : selectedOrder ? (
          <Box>
            {/* ── Order Info Section ── */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.75, sm: 2 } }}>
              <Grid container spacing={0}>
                <Grid item xs={6}>
                  <InfoRow icon={<PersonIcon />} label="Customer">
                    <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '0.88rem' }}>
                      {selectedOrder.customer_name || 'Guest'}
                    </Typography>
                  </InfoRow>
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<TableIcon />} label="Table">
                    <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '0.88rem' }}>
                      {getTableName(selectedOrder)}
                    </Typography>
                  </InfoRow>
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<ScheduleIcon />} label="Order Date">
                    <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.82rem' }}>
                      {formatShortDate(selectedOrder.created_at)}
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: '#6b7280', fontSize: '0.75rem' }}>
                      {formatTime(selectedOrder.created_at)}
                    </Typography>
                  </InfoRow>
                </Grid>
                <Grid item xs={6}>
                  <InfoRow icon={<ReceiptIcon />} label="Total">
                    <Typography sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: '#1f2937',
                    }}>
                      {formatCurrency(Number(selectedOrder.total || selectedOrder.total_price) || 0)}
                    </Typography>
                  </InfoRow>
                </Grid>
                {selectedOrder.customer_email && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow icon={<EmailIcon />} label="Email">
                      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.82rem' }}>
                        {selectedOrder.customer_email}
                      </Typography>
                    </InfoRow>
                  </Grid>
                )}
                {selectedOrder.customer_phone && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow icon={<PhoneIcon />} label="Phone">
                      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.82rem' }}>
                        {selectedOrder.customer_phone}
                      </Typography>
                    </InfoRow>
                  </Grid>
                )}
              </Grid>

              {selectedOrder.notes && (
                <Box sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: '#fffbeb',
                  border: '1px solid #fef3c7',
                  borderRadius: '12px',
                  px: 2,
                  py: 1.5,
                }}>
                  <NotesIcon sx={{ fontSize: '1rem', color: '#f59e0b', mt: 0.25 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Order Notes
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#78350f', mt: 0.25, lineHeight: 1.5 }}>
                      {selectedOrder.notes}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedOrder.cancel_reason && (
                <Box sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '12px',
                  px: 2,
                  py: 1.5,
                }}>
                  <CancelIcon sx={{ fontSize: '1rem', color: '#ef4444', mt: 0.25 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Cancellation Reason
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#991b1b', mt: 0.25, lineHeight: 1.5 }}>
                      {selectedOrder.cancel_reason}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ── Order Items ── */}
            <Box sx={{
              bgcolor: '#f9fafb',
              px: { xs: 2, sm: 2.5 },
              py: 2.5,
              borderTop: '1px solid rgba(0,0,0,0.06)',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CartIcon sx={{ fontSize: '1.1rem', color: '#667eea' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
                  Items ({orderItems.length})
                </Typography>
              </Box>

              {isPending && canAddEditOrderItems() && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setAddItemOpen(true)}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: '#667eea',
                      borderRadius: '10px',
                      px: 2,
                      '&:hover': { bgcolor: '#eef2ff' },
                    }}
                  >
                    Add Item
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1 } }}>
                {orderItems.map((item) => {
                  const isDeleting = deletingItemId === item.id;
                  const isUpdating = updatingItemId === item.id;
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: { xs: '10px', sm: '12px' },
                        border: '1px solid rgba(0,0,0,0.06)',
                        p: { xs: 1.25, sm: 1.75 },
                        opacity: isDeleting ? 0.5 : 1,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.25 }, flex: 1, minWidth: 0, alignItems: 'center' }}>
                          {isPending && canAddEditOrderItems() ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '3px' }}>
                              <IconButton
                                size="small"
                                disabled={isUpdating || item.quantity <= 1}
                                onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                                sx={{
                                  width: { xs: 22, sm: 26 },
                                  height: { xs: 22, sm: 26 },
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                              </IconButton>
                              <Box sx={{
                                minWidth: { xs: 28, sm: 32 },
                                height: { xs: 24, sm: 28 },
                                borderRadius: '6px',
                                bgcolor: `${statusClr.color}14`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 0.5,
                              }}>
                                {isUpdating ? (
                                  <CircularProgress size={12} />
                                ) : (
                                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.72rem', sm: '0.8rem' }, color: statusClr.color }}>
                                    {item.quantity}x
                                  </Typography>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                disabled={isUpdating}
                                onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                                sx={{
                                  width: { xs: 22, sm: 26 },
                                  height: { xs: 22, sm: 26 },
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                }}
                              >
                                <AddIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{
                              minWidth: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              borderRadius: '8px',
                              bgcolor: `${statusClr.color}14`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              px: 0.5,
                            }}>
                              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.72rem', sm: '0.8rem' }, color: statusClr.color }}>
                                {item.quantity}x
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.82rem', sm: '0.88rem' }, color: '#1f2937', lineHeight: 1.3 }}>
                              {item.name ||
                                item.menu_item_name ||
                                (typeof item.menu_item === 'object'
                                  ? item.menu_item?.name
                                  : item.menu_item) ||
                                'Unknown Item'}
                            </Typography>
                            {item.options && item.options.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <Chip
                                  label={`Base ${formatCurrency(Number(item.price) || 0)}`}
                                  size="small"
                                  sx={{
                                    height: { xs: '20px', sm: '22px' },
                                    fontSize: { xs: '0.62rem', sm: '0.68rem' },
                                    fontWeight: 500,
                                    bgcolor: 'rgba(0,0,0,0.05)',
                                    color: '#6b7280',
                                  }}
                                />
                                {item.options.map((opt, optIdx) => (
                                  <Chip
                                    key={optIdx}
                                    label={`${opt.name}${Number(opt.price_addition) > 0 ? ` +${formatCurrency(Number(opt.price_addition))}` : ''}`}
                                    size="small"
                                    sx={{
                                      height: { xs: '20px', sm: '22px' },
                                      fontSize: { xs: '0.62rem', sm: '0.68rem' },
                                      fontWeight: 500,
                                      bgcolor: 'rgba(102,126,234,0.08)',
                                      color: '#667eea',
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                            {item.notes && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <NotesIcon sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: '#f59e0b', flexShrink: 0 }} />
                                <Typography sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  color: '#92400e',
                                  fontStyle: 'italic',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {item.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: { xs: 0.75, sm: 1.5 } }}>
                          <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.88rem' }, color: '#1f2937' }}>
                            {formatCurrency(Number(item.total))}
                          </Typography>
                          {isPending && canAddEditOrderItems() && (
                            <Tooltip title="Remove item">
                              <IconButton
                                size="small"
                                disabled={isDeleting}
                                onClick={() => handleDeleteItem(item.id)}
                                sx={{
                                  color: '#ef4444',
                                  width: { xs: 24, sm: 28 },
                                  height: { xs: 24, sm: 28 },
                                  '&:hover': { bgcolor: '#fef2f2' },
                                }}
                              >
                                {isDeleting ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <DeleteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* ── Order Summary ── */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.75, sm: 2 } }}>
              <Box sx={{
                bgcolor: '#f9fafb',
                borderRadius: '14px',
                border: '1px solid rgba(0,0,0,0.06)',
                p: 2.5,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                    {formatCurrency(Number(selectedOrder.subtotal) || 0)}
                  </Typography>
                </Box>
                {Number(selectedOrder.tax) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Tax</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                      {formatCurrency(Number(selectedOrder.tax))}
                    </Typography>
                  </Box>
                )}
                {Number(selectedOrder.service_charge) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Service Charge</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                      {formatCurrency(Number(selectedOrder.service_charge))}
                    </Typography>
                  </Box>
                )}
                {Number(selectedOrder.platform_service_fee) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Platform Fee</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                      {formatCurrency(Number(selectedOrder.platform_service_fee))}
                    </Typography>
                  </Box>
                )}
                {selectedOrder.discount_details && selectedOrder.discount_details.length > 0 && (
                  <>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', mt: 0.5, mb: 0.5 }}>
                      Discounts
                    </Typography>
                    {selectedOrder.discount_details.map((disc) => (
                      <Box key={disc.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, pl: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.82rem', color: '#16a34a' }}>
                            {disc.name}
                          </Typography>
                          {disc.is_auto && (
                            <Chip
                              label="Auto"
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8' }}
                            />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#16a34a' }}>
                          -{formatCurrency(Number(disc.amount))}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
                {Number(selectedOrder.discount) > 0 &&
                  (!selectedOrder.discount_details || selectedOrder.discount_details.length === 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>Discount</Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a' }}>
                        -{formatCurrency(Number(selectedOrder.discount))}
                      </Typography>
                    </Box>
                  )}
                <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1f2937' }}>Total</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1f2937' }}>
                    {formatCurrency(Number(selectedOrder.total || selectedOrder.total_price) || 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── GCash Receipt ── */}
            {gcashPayment && (
              <Box sx={{
                px: { xs: 2, sm: 2.5 },
                pb: 2.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ReceiptIcon sx={{ fontSize: '1.1rem', color: '#667eea' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
                    GCash Receipt
                  </Typography>
                </Box>
                <GcashReceiptPreview
                  receiptImageUrl={gcashPayment.receipt_image_url}
                  receiptData={gcashPayment.receipt_data}
                  formatCurrency={formatCurrency}
                />
              </Box>
            )}

            {/* ── Status History ── */}
            <Box sx={{
              px: { xs: 2, sm: 2.5 },
              pb: 2.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTimeIcon sx={{ fontSize: '1.1rem', color: '#667eea' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
                  Status History
                </Typography>
              </Box>

              {statusHistory.length > 0 ? (
                <Box sx={{ position: 'relative', pl: 2.5 }}>
                  {/* Timeline line */}
                  <Box sx={{
                    position: 'absolute',
                    left: 11,
                    top: 12,
                    bottom: 12,
                    width: 2,
                    bgcolor: 'rgba(0,0,0,0.08)',
                    borderRadius: 1,
                  }} />

                  {statusHistory.map((history, index) => {
                    const historyClr = getOrderStatusColor(history.status);
                    const isFirst = index === 0;
                    return (
                      <Box
                        key={history.id}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mb: index < statusHistory.length - 1 ? 2.5 : 0,
                          position: 'relative',
                        }}
                      >
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: isFirst ? historyClr.color : '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          zIndex: 1,
                          border: isFirst ? `2px solid ${historyClr.color}40` : 'none',
                          boxShadow: isFirst ? `0 0 0 4px ${historyClr.color}15` : 'none',
                        }}>
                          {React.cloneElement(getStatusIcon(history.status), {
                            sx: { fontSize: '0.75rem', color: isFirst ? 'white' : '#9ca3af' }
                          })}
                        </Box>

                        <Box sx={{
                          flex: 1,
                          bgcolor: isFirst ? `${historyClr.color}08` : 'transparent',
                          borderRadius: '10px',
                          border: isFirst ? `1px solid ${historyClr.color}18` : 'none',
                          p: isFirst ? 1.5 : 0,
                          pt: isFirst ? 1.5 : 0.25,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={getOrderStatusLabel(history.status)}
                              size="small"
                              sx={{
                                bgcolor: historyClr.bg,
                                color: historyClr.color,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: '22px',
                              }}
                            />
                            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>
                              {formatShortDateTime(history.created_at)}
                            </Typography>
                          </Box>

                          {history.created_by && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', mt: 0.5 }}>
                              by {history.created_by.full_name || history.created_by.email || 'Staff'}
                            </Typography>
                          )}

                          {history.notes && (
                            <Typography sx={{
                              mt: 0.75,
                              fontSize: '0.8rem',
                              color: '#4b5563',
                              fontStyle: 'italic',
                              bgcolor: 'white',
                              borderRadius: '8px',
                              border: '1px solid rgba(0,0,0,0.06)',
                              px: 1.5,
                              py: 1,
                            }}>
                              {history.notes}
                            </Typography>
                          )}

                          {history.previous_status && (
                            <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', mt: 0.5 }}>
                              from {history.previous_status.replace('_', ' ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 3,
                  bgcolor: '#f9fafb',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.06)',
                }}>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                    No status history available
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ── Update Status Form ── */}
            {dialogType === 'update' && (
              <Box sx={{
                px: { xs: 2, sm: 2.5 },
                pb: 2.5,
                borderTop: '1px solid rgba(0,0,0,0.06)',
                pt: 2.5,
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', mb: 2 }}>
                  Update Status
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={statusFormData.status}
                    onChange={(e) =>
                      setStatusFormData((prev) => ({ ...prev, status: e.target.value }))
                    }
                    label="Status"
                    sx={{ borderRadius: '12px' }}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="notes"
                  label="Notes"
                  value={statusFormData.notes}
                  onChange={(e) =>
                    setStatusFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Add any notes about this status change..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.5, sm: 1.75 },
        flexShrink: 0,
        borderTop: '1px solid rgba(0,0,0,0.06)',
        gap: 1,
      }}>
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderRadius: '12px',
            color: '#6b7280',
          }}
        >
          Close
        </Button>
        {dialogType === 'update' && (
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            sx={{
              background: '#667eea',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderRadius: '12px',
              boxShadow: 'none',
              '&:hover': {
                background: '#5568d3',
                boxShadow: '0 4px 12px rgba(102,126,234,0.35)',
              },
            }}
          >
            Update Status
          </Button>
        )}
      </DialogActions>

      <AddOrderItemDialog
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        orderId={orderId}
        onItemAdded={handleItemAdded}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default OrderDetailDialog;
